// Reads through row-level security as the signed-in user.
import { env } from '../env.ts';
import { invoke, operatorClient } from './supabase.ts';
import type { AssumptionEntry, EvaluationRow, NodeRow, ReviewRow, ValueRow } from './types.ts';

async function rows<T>(q: PromiseLike<{ data: unknown; error: { message: string } | null }>): Promise<T[]> {
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as T[];
}

export const productBatches = () =>
  rows<NodeRow>(
    operatorClient()
      .from('batch_nodes')
      .select('node_id, kind, prod_code, owner_org')
      .eq('kind', 'product_batch')
      .order('node_id'),
  );

export const allNodes = () =>
  rows<NodeRow>(operatorClient().from('batch_nodes').select('node_id, kind, prod_code, owner_org'));

export const links = () =>
  rows<{ from_node: string; link: string; to_node: string | null; same_as: string | null }>(
    operatorClient().from('current_node_links').select('from_node, link, to_node, same_as'),
  );

export const valuesFor = (nodeIds: string[]) =>
  rows<ValueRow>(
    operatorClient().from('current_values').select('*').in('node_id', nodeIds).order('field_id'),
  );

export const latestEvaluations = async (): Promise<Map<string, EvaluationRow>> => {
  const list = await rows<EvaluationRow>(
    operatorClient()
      .from('evaluations_visible')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500),
  );
  const latest = new Map<string, EvaluationRow>();
  for (const e of list) if (!latest.has(`${e.batch_id}|${e.mode}`)) latest.set(`${e.batch_id}|${e.mode}`, e);
  return latest;
};

export const evaluationsFor = (batchId: string) =>
  rows<EvaluationRow>(
    operatorClient()
      .from('evaluations_visible')
      .select('*')
      .eq('batch_id', batchId)
      .order('created_at', { ascending: false })
      .limit(50),
  );

export const evidence = () =>
  rows<Record<string, unknown>>(
    operatorClient()
      .from('evidence')
      .select('*, evidence_links(value_id, rule_id, batch_id)')
      .order('evidence_id')
      .order('version'),
  );

export const reviewsFor = async (valueIds: number[]): Promise<Map<number, ReviewRow[]>> => {
  const out = new Map<number, ReviewRow[]>();
  if (valueIds.length === 0) return out;
  const list = await rows<ReviewRow>(
    operatorClient().from('value_reviews').select('*').in('value_id', valueIds).order('review_id'),
  );
  for (const r of list) out.set(r.value_id, [...(out.get(r.value_id) ?? []), r]);
  return out;
};

export const profiles = async (): Promise<Map<string, string>> =>
  new Map(
    (
      await rows<{ user_id: string; display_name: string }>(
        operatorClient().from('profiles').select('user_id, display_name'),
      )
    ).map((p) => [p.user_id, p.display_name]),
  );

export const myRoles = async (): Promise<string[]> => {
  const { data } = await operatorClient().auth.getUser();
  if (!data.user) return [];
  return (
    await rows<{ role: string }>(
      operatorClient().from('user_roles').select('role').eq('user_id', data.user.id),
    )
  ).map((r) => r.role);
};

/** The part of a rule definition the interface reads to state a time-conditioned obligation (US-28). */
export interface ScheduledRuleDef {
  rule_id: string;
  criterion: string;
  severity_schedule?: { from?: string; until?: string; severity: string }[];
}

export interface ActivePack {
  pack_hash: string;
  pack: {
    route_model: { route_model_version: string; assumption_catalogue: AssumptionEntry[] };
    rules: { meta: { overlay_applied?: string }; items: ScheduledRuleDef[] };
  };
}

export const activePack = async (): Promise<ActivePack | null> => {
  const { data, error } = await operatorClient().rpc('active_config_pack');
  if (error) throw new Error(error.message);
  const row = (Array.isArray(data) ? data[0] : data) as ActivePack | null;
  return row?.pack_hash ? row : null;
};

export const pendingRequests = () =>
  rows<{ request_id: number; batch_id: string; reason: string; requested_at: string }>(
    operatorClient()
      .from('evaluation_requests')
      .select('request_id, batch_id, reason, requested_at')
      .is('processed_at', null)
      .order('requested_at', { ascending: false })
      .limit(100),
  );

// In-app notice of evidence changes (US-26 Should, until e-mail); row-level security limits it to the audience role.
export const notices = () =>
  rows<{ notification_id: number; kind: string; payload: Record<string, unknown>; created_at: string }>(
    operatorClient()
      .from('notifications')
      .select('notification_id, kind, payload, created_at')
      .order('created_at', { ascending: false })
      .limit(20),
  );

export const whoAmI = async (): Promise<{ name: string; org: string | null; roles: string[] } | null> => {
  const { data } = await operatorClient().auth.getUser();
  if (!data.user) return null;
  const p = await rows<{ display_name: string; org_code: string | null }>(
    operatorClient().from('profiles').select('display_name, org_code').eq('user_id', data.user.id),
  );
  return { name: p[0]?.display_name ?? '', org: p[0]?.org_code ?? null, roles: await myRoles() };
};

export interface PassportRow {
  upi: string;
  batch_id: string;
  demonstration: boolean;
  published_at: string;
  status: string;
}

/** Published passports, newest first; the latest one per batch is what the screens link to. */
export const passports = async (): Promise<Map<string, PassportRow>> => {
  const list = await rows<PassportRow>(
    operatorClient()
      .from('passports')
      .select('upi, batch_id, demonstration, published_at, status')
      .eq('status', 'published')
      .order('published_at', { ascending: false }),
  );
  const latest = new Map<string, PassportRow>();
  for (const p of list) if (!latest.has(p.batch_id)) latest.set(p.batch_id, p);
  return latest;
};

export interface DemoScenario {
  name: string;
  test: string;
  title: string;
  batch_id: string;
  mode: string;
  assumptions: string[];
}

/** The acceptance scenarios of the demo seed; the data environment has none, so any refusal reads as an empty list. */
export const demoScenarios = async (): Promise<DemoScenario[]> => {
  if (env.environment !== 'demo') return [];
  try {
    return (await invoke<{ scenarios: DemoScenario[] }>('admin-reset', { action: 'list' })).scenarios;
  } catch {
    return [];
  }
};
